/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import type { JSX } from "react"
import { useState } from "react"

import { canManageRole } from "../../features/access-rights/access-rights"
import { OrganizationViewModel } from "../../features/organization/organization-viewmodel"
import { UserDetailViewModel } from "../../features/user/user-detail-view-model"
import { formatRole, UserRole } from "../../features/user/user-types"
import { LoggerViewModel } from "../../logger/logger-viewmodel"
import { useAppState, useViewModel, useViewModelWithArgs } from "../app/app-hooks"

import "./user-detail-card.css"

interface UserDetailCardProps {
    userId: number
    orgId: number
    onShowDangerZone: () => void
}

export const UserDetailCard = observer(({ userId, orgId, onShowDangerZone }: UserDetailCardProps): JSX.Element => {
    const userVM = useViewModelWithArgs(UserDetailViewModel, userId)
    const loggerVM = useViewModel(LoggerViewModel)
    const orgVM = useViewModel(OrganizationViewModel)
    const roles = useAppState().roles
    const user = userVM.userDetail

    const [newRole, setNewRole] = useState("")
    const [isEditing, setIsEditing] = useState(false)
    const [editedName, setEditedName] = useState("")

    if (!user) {
        return <></>
    }

    const handleUpdateUser = async () => {
        setIsEditing(false)
        const result = await userVM.updateOrganizationUser(editedName)
        if (!result.success) {
            loggerVM.logErrorMessage("Failed to update user.")
        }
    }

    const handleAddRole = async () => {
        if (userVM.isValidUserRole(newRole)) {
            const result = await userVM.assignUserRole(newRole)
            setNewRole("")
            if (!result.success) {
                loggerVM.logErrorMessage("Failed to add Role.")
            }
        } else {
            loggerVM.logErrorMessage(`Invalid role: ${newRole}`)
        }
    }

    const handleRemoveRole = async (role: string) => {
        if (userVM.isValidUserRole(role)) {
            const result = await userVM.removeUserRole(role)
            if (!result.success) {
                loggerVM.logErrorMessage("Failed to remove Role.")
            }
        } else {
            loggerVM.logErrorMessage(`Invalid role: ${role}`)
        }
    }

    return (
        <div className="user-detail-card card shadow-legacy-sm">
            <div className="card-header custom-card-header flex justify-between items-center">
                <h5 className="mb-0">User Detail</h5>

                {user.enabled && (
                    <button
                        type="button"
                        className="btn btn-outline-danger no-inherit-bg btn-sm"
                        onClick={onShowDangerZone}
                    >
                        Admin Actions
                    </button>
                )}
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-lg-6">
                        <table className="table table-sm mb-0">
                            <tbody>
                                <tr>
                                    <td className="td-label">ID:</td>
                                    <td>{userId ?? <span className="text-legacy-muted">Not available</span>}</td>
                                </tr>
                                <tr>
                                    <td className="td-label">Name:</td>
                                    <td>
                                        {isEditing ? (
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    className="name-input form-control form-control-sm"
                                                    value={editedName}
                                                    onChange={(e) => setEditedName(e.target.value)}
                                                    autoFocus
                                                />
                                                <button
                                                    className="btn btn-sm btn-outline-success"
                                                    onClick={handleUpdateUser}
                                                    disabled={!editedName.trim()}
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => setIsEditing(false)}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <div
                                                className="flex justify-between items-center"
                                                onClick={() => {
                                                    if (user.enabled === false) return
                                                    setIsEditing(true)
                                                    setEditedName(user.name ?? "")
                                                }}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <span>
                                                    {user.name || (
                                                        <span className="text-legacy-muted">Not available</span>
                                                    )}
                                                </span>
                                                {user.enabled !== false && (
                                                    <button className="btn-change-username btn btn-sm btn-outline-primary">
                                                        Change
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="td-label">Username:</td>
                                    <td>{user.username ?? <span className="text-legacy-muted">Not available</span>}</td>
                                </tr>
                                {user.organizationId === orgId && (
                                    <tr>
                                        <td className="td-label">Organization:</td>
                                        <td>
                                            {orgVM.selectedOrganizationDetail?.name ?? (
                                                <span className="text-legacy-muted">None</span>
                                            )}
                                        </td>
                                    </tr>
                                )}
                                <tr>
                                    <td className="td-label">Enabled:</td>
                                    <td>
                                        {user.enabled === true ? (
                                            "✓ Enabled"
                                        ) : user.enabled === false ? (
                                            "✗ Disabled"
                                        ) : (
                                            <span className="text-legacy-muted">Undefined</span>
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {!(user.external || user.organizationId !== orgId) && (
                        <div className="col-lg-6">
                            <div className="roles-container">
                                <div className="mb-legacy roles-title">Roles:</div>
                                {userVM.userDetailRoles.length > 0 ? (
                                    userVM.userDetailRoles.map((r) => (
                                        <div key={r} className="mb-2">
                                            {user.enabled === false ? (
                                                <span className="form-control-sm bg-legacy-light text-legacy-muted">
                                                    {formatRole(r)}
                                                </span>
                                            ) : (
                                                <div className="input-group input-group-sm">
                                                    <input className="form-control" value={formatRole(r)} readOnly />
                                                    <button
                                                        className="btn btn-outline-danger"
                                                        onClick={() => handleRemoveRole(r)}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <span className="text-legacy-muted">No roles assigned</span>
                                )}

                                {user.enabled && (
                                    <div className="mt-legacy">
                                        <label className="form-label">Add Role</label>
                                        <div className="input-group input-group-sm">
                                            <select
                                                className="form-select"
                                                value={newRole}
                                                onChange={(e) => setNewRole(e.target.value)}
                                            >
                                                <option value="">Select a role...</option>
                                                {Object.values(UserRole).map((role) => {
                                                    const assigned = userVM.userDetailRoles.includes(role)
                                                    const grantable = canManageRole(roles, role)
                                                    return (
                                                        <option
                                                            key={role}
                                                            value={role}
                                                            disabled={assigned || !grantable}
                                                        >
                                                            {formatRole(role)}
                                                        </option>
                                                    )
                                                })}
                                            </select>
                                            <button
                                                className="btn btn-outline-success"
                                                disabled={!newRole}
                                                onClick={() => handleAddRole()}
                                            >
                                                Add
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
})
